<template>
  <TypeformQuestion :question="`Autocomplete with Resume ${emoji}`" center
                    subtitle="Drag and drop or click to upload."
                    :loading="validating"
                    @prevStep="() => $emit('prevStep')"
                    @nextStep="() => $emit('nextStep')">

    <div class="space-y-6 text-left bg-white p-4 lg:p-6 rounded-md border border-slate-200">

      <template v-if="parseResumeStore.resume.file && parseResumeStore.resume.fileDataUrl">

        <ResumePreview
          class="w-full sm:w-80 mx-auto"
          v-model:resume-file="parseResumeStore.resume.file"
          v-model:resume-data-url="parseResumeStore.resume.fileDataUrl"
          :formatted-file-size="parseResumeStore.resume.formattedSize"
          :file-name="parseResumeStore.resume.name"
          :primary-background="typeformData.primaryBackground"
          :primary-foreground="typeformData.primaryForeground"
        />

      </template>

      <template v-else>
        <UploadResume
          v-model:file="parseResumeStore.resume.file"
          v-model:fileDataUrl="parseResumeStore.resume.fileDataUrl"
          v-model:fileName="parseResumeStore.resume.name"
          v-model:formattedFileSize="parseResumeStore.resume.formattedSize"
          :backgroundHex="typeformData.primaryBackground"
          :foregroundHex="typeformData.primaryForeground"
        />

        <div class="h-px w-full bg-slate-100"/>

        <button type="button" @click="$emit('toggleUseBdResume')"
                class="font-semibold px-6 text-sm block mx-auto py-3 border rounded-lg opacity-60 hover:opacity-100 transition-all"
                :style="buttonOutline">
          Don't have a resume? Apply without one
        </button>
      </template>

    </div>

    <FormCheckbox
      id="resumeTos"
      class="text-base max-w-[90vw] sm:max-w-md mx-auto" custom-colors
      v-if="parseResumeStore.resume.file && parseResumeStore.resume.fileDataUrl"
      v-model="store.newUserDetails.tosChecked">
      I have read and agree to the
      <a href="/privacy-policy" target="_blank" tabindex="-1"><u>Privacy Policy</u></a> and
      <a href="/terms-of-service" target="_blank" tabindex="-1"><u>Terms of Service</u></a>
      and represent and confirm that I am at least 16 years of age and legally authorized to work in the
      United States; this site is protected by reCAPTCHA and the Google
      <a href="https://policies.google.com/privacy" rel="nofollow" target="_blank" tabindex="-1"><u>Privacy Policy</u></a>
      and <a href="https://policies.google.com/terms" rel="nofollow" target="_blank" tabindex="-1"><u>Terms of Service</u></a> apply.
    </FormCheckbox>

  </TypeformQuestion>
</template>

<script setup>
import FormCheckbox from "./FormCheckbox.vue";
import ResumePreview from "./ResumePreview.vue";
import TypeformQuestion from "./TypeformQuestion.vue";
import UploadResume from "./UploadResume.vue";
import {computed, inject, onMounted} from "vue";
import {darkestHex, getContrastRatio} from "../helpers/Color";
import {useCandidateApplicationFormStore} from "../stores/CandidateApplicationFormStore";
import {useParseResumeStore} from "../stores/ParseResumeStore";

defineProps({validating: Boolean});
const emits = defineEmits(["prevStep", "nextStep", "toggleUseBdResume", "update:validating", "update:validator"]);
const typeformData = inject("typeformData", {});
const store = useCandidateApplicationFormStore();
const parseResumeStore = useParseResumeStore();

const buttonOutline = computed(() => `border-color: ${darkestHex(typeformData.primaryBackground, typeformData.primaryForeground)};color:${darkestHex(typeformData.primaryBackground, typeformData.primaryForeground)};`);

const possibleEmojis = [
  {emoji: "🚀", hexColors: ["#fff54d", "#f40200", "#d7e3d8"]},
  {emoji: "✨", hexColors: ["#fbdd3a"]},
  {emoji: "🎯", hexColors: ["#de1318"]},
  {emoji: "⏩", hexColors: ["#7ea2c3"]},
  {emoji: "💎", hexColors: ["#53a2e7"]},
];

const emoji = computed(() => {
  let index = 0;
  let bestContrastEmoji = "";

  while (bestContrastEmoji.length < 1 && index < possibleEmojis.length) {
    const data = possibleEmojis[index];
    let lowestContrast = 21;

    for (let i = 1; i < data.hexColors.length; i++) {
      const indexContrast = getContrastRatio(typeformData.primaryBackground, data.hexColors[i]);
      if (indexContrast < lowestContrast) lowestContrast = indexContrast;
    }

    if (lowestContrast >= 4) bestContrastEmoji += data.emoji;

    index++;
  }

  return bestContrastEmoji;
});

onMounted(() => {
  store.setFurthestStep(1, "Auto-complete with Resume");

  emits("update:validating", false);
  emits("update:validator", async () => {
    const hasResume = !!parseResumeStore.resume.file;
    const tosChecked = store.newUserDetails.tosChecked;

    if (!hasResume) return false;

    if (!tosChecked) {
      toast.warning("Please agree to the terms of service and privacy policy to continue.");
      return false;
    }

    emits("update:validating", true);
    toast.success("Auto-completing... (takes ~2-8 seconds) ✨");
    let success = false;

    try {
      const data = await parseResumeStore.parseContactInfo();

      if (data) {
        const isPhoneString = typeof data.phone === "string";

        store.newUserDetails.firstName = data.firstName;
        store.newUserDetails.lastName = data.lastName;
        store.newUserDetails.email = data.email;
        store.newUserDetails.phone = isPhoneString ? parseInt(data.phone.replace(/\D/g, "")) : data.phone;
        store.newUserDetails.city = data.usaCity;
        store.newUserDetails.state = data.usaState;
        store.newUserDetails.username = data.username;
        success = true;
      }

      if (success) toast.success("Auto-complete done! 🎉");
    } catch (e) {
      console.error(e);
    } finally {
      emits("update:validating", false);
    }

    return success;
  });
});
</script>
